/**
 * Financial report — `GET reports/financial/`, manager roles only. All money
 * fields are plain JSON numbers (a hand-written `APIView`, not a
 * `ModelSerializer`). `income_trend[].month` is an ISO-8601 datetime;
 * `expense_trend[].month` is `YYYY-MM-DD` — the two genuinely differ in shape
 * (see the field-level note in src/types/report.ts). No chart library in this
 * project; rendered as plain tables.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { parseApiError } from '../../lib/apiError';
import { getFinancialReport } from '../../api/endpoints/reports';

export function FinancialReportPage(): React.JSX.Element {
  const query = useQuery({
    queryKey: ['reports-financial'],
    queryFn: () => getFinancialReport(),
  });

  /** Client-side PDF, same shared chrome module `InvoiceDetailPage.tsx`'s "Print invoice" uses — no server-side PDF endpoint. */
  const exportPdf = useMutation({
    mutationFn: async () => {
      const report = query.data;
      if (report === undefined) return;

      const { jsPDF } = await import('jspdf');
      const { createBrandedDocument, drawTable, downloadPdf, formatPdfMoney } = await import('../../lib/pdf');

      const { doc, contentStartY } = createBrandedDocument(jsPDF, 'FINANCIAL REPORT');
      let y = contentStartY;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.text(`Total collected: ${formatPdfMoney(report.total_collected)}`, 15, y);
      y += 6;
      doc.text(`Total expenses: ${formatPdfMoney(report.total_expenses)}`, 15, y);
      y += 6;
      doc.text(`Outstanding balance: ${formatPdfMoney(report.outstanding_balance)}`, 15, y);
      y += 6;
      doc.text(`Collection efficiency: ${report.collection_efficiency.toFixed(2)}%`, 15, y);
      y += 10;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('Income trend', 15, y);
      y += 4;
      y = drawTable(doc, {
        startY: y,
        head: ['Month', 'Total'],
        rows: report.income_trend.map((row) => [row.month, formatPdfMoney(row.total)]),
      });
      y += 10;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('Expense trend', 15, y);
      y += 4;
      drawTable(doc, {
        startY: y,
        head: ['Month', 'Total'],
        rows: report.expense_trend.map((row) => [row.month, formatPdfMoney(row.total)]),
      });

      downloadPdf(doc, 'financial-report.pdf');
    },
  });

  if (query.isPending) return <p>Loading…</p>;
  if (query.isError) {
    return (
      <p role="alert" className="form__error">
        {parseApiError(query.error).message}
      </p>
    );
  }

  const report = query.data;

  return (
    <section>
      <h2>Financial report</h2>

      <p>
        <button type="button" disabled={exportPdf.isPending} onClick={() => exportPdf.mutate()}>
          {exportPdf.isPending ? 'Exporting…' : 'Export PDF'}
        </button>
      </p>

      {exportPdf.isError && (
        <p role="alert" className="form__error">
          {parseApiError(exportPdf.error).message}
        </p>
      )}

      <dl>
        <dt>Total collected</dt>
        <dd>{report.total_collected}</dd>
        <dt>Total expenses</dt>
        <dd>{report.total_expenses}</dd>
        <dt>Outstanding balance</dt>
        <dd>{report.outstanding_balance}</dd>
        <dt>Collection efficiency</dt>
        <dd>{report.collection_efficiency.toFixed(2)}%</dd>
      </dl>

      <h3>Income trend</h3>
      <table>
        <thead>
          <tr>
            <th>Month</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {report.income_trend.map((row) => (
            <tr key={row.month}>
              <td>{row.month}</td>
              <td>{row.total}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3>Expense trend</h3>
      <table>
        <thead>
          <tr>
            <th>Month</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {report.expense_trend.map((row) => (
            <tr key={row.month}>
              <td>{row.month}</td>
              <td>{row.total}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

export default FinancialReportPage;
